import React from 'react';
import { Button } from '@/components/ui/Buttons';
import Pill from '@/components/ui/Pill';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import { absoluteLabel } from './timeFormat';
import type { AnalysisState, AnalysisVerdict, LiveSessionAnalysis, ParticipantStudent } from './types';

interface AnalysisPanelProps {
  analysis: LiveSessionAnalysis | null | undefined;
  error: string | null;
  isRunning: boolean;
  students: ParticipantStudent[];
  onEvidenceClick: (eventId: number) => void;
  onRun: () => void;
  onSelectStudent: (studentId: number) => void;
}

type PillColor = 'green' | 'grey' | 'red' | 'yellow';

// Plain-English labels: the panel is read by support staff, not by the engineers who named the states.
const STATE_PILL: Record<AnalysisState, { color: PillColor; label: string; solid: boolean }> = {
  free: { color: 'yellow', label: 'unlocked', solid: true },
  healthy: { color: 'green', label: 'fine', solid: false },
  left_early: { color: 'grey', label: 'left early', solid: false },
  never_joined: { color: 'grey', label: 'never joined', solid: false },
  stuck: { color: 'red', label: 'stuck', solid: true },
  unknown: { color: 'grey', label: 'unclear', solid: false },
};

const STATE_ORDER: AnalysisState[] = ['stuck', 'free', 'unknown', 'left_early', 'never_joined', 'healthy'];

const MECHANISM_LABEL: Record<string, string> = {
  deck_not_loaded: 'slides had not loaded yet',
  late_joiner_after_reload: 'joined late after a reload',
  nav_dropped_while_disconnected: 'a slide change never reached them',
  none: '',
  other: 'something else',
  removed_by_teacher: 'removed by the teacher',
  socket_drop_while_locked: 'lost connection while locked',
  teacher_drop: 'the teacher lost connection',
  two_tabs: 'had two tabs open',
  uninvited_direct_url: 'opened the slideshow without an invite',
};

const PANEL_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-4',
  'tw-flex tw-flex-col tw-gap-4',
);
const LINK_BUTTON_CLASS = cn(
  'tw-bg-transparent tw-border-0 tw-p-0 tw-text-xs tw-text-tci-purple tw-cursor-pointer hover:tw-underline',
);

const PRIVACY_NOTE = 'Written by an AI model from a copy of this event log with every name, id, URL, and browser '
  + 'detail removed. Nothing that identifies a person leaves TCI.';

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Text className="tw-text-tci-black tw-m-0" size="sm" tag="h3" weight="bold">{children}</Text>
);

interface EvidenceLinksProps {
  confidence: AnalysisVerdict['confidence'];
  ids: number[];
  onClick: (eventId: number) => void;
}

const EvidenceLinks = ({ confidence, ids, onClick }: EvidenceLinksProps) => (
  <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
    <Text className="tw-text-very-dark-grey tw-m-0" size="xs" tag="span">
      Confidence: {confidence}.{ids.length > 0 ? ' Based on events' : ''}
    </Text>
    {ids.map(id => (
      <button
        key={id}
        aria-label={`Show event ${id} in the timeline`}
        className={LINK_BUTTON_CLASS}
        type="button"
        onClick={() => onClick(id)}
      >
        #{id}
      </button>
    ))}
  </div>
);

interface VerdictRowProps {
  student: ParticipantStudent | undefined;
  verdict: AnalysisVerdict;
  onEvidenceClick: (eventId: number) => void;
  onSelectStudent: (studentId: number) => void;
}

const VerdictRow = ({ student, verdict, onEvidenceClick, onSelectStudent }: VerdictRowProps) => {
  const pill = STATE_PILL[verdict.state];
  const mechanism = MECHANISM_LABEL[verdict.mechanism] ?? verdict.mechanism;
  return (
    <li className="tw-flex tw-flex-col tw-gap-1 tw-py-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <button
          className={cn(LINK_BUTTON_CLASS, 'tw-text-sm tw-font-bold')}
          type="button"
          onClick={() => onSelectStudent(verdict.student_id)}
        >
          {student?.full_name ?? `Student ${verdict.student_id}`}
        </button>
        <Pill pillColor={pill.color} solid={pill.solid}>
          <Text size="xs" tag="span" weight="bold">{pill.label}</Text>
        </Pill>
        {mechanism && verdict.state !== 'healthy' && (
          <Text className="tw-text-very-dark-grey tw-m-0" size="sm" tag="span">{mechanism}</Text>
        )}
      </div>
      {verdict.explanation && <Text className="tw-text-tci-black tw-m-0" size="sm">{verdict.explanation}</Text>}
      <EvidenceLinks confidence={verdict.confidence} ids={verdict.evidence_event_ids} onClick={onEvidenceClick} />
    </li>
  );
};

const GeneratedMeta = ({ analysis }: { analysis: LiveSessionAnalysis }) => (
  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
    <Text className="tw-text-very-dark-grey tw-m-0" size="xs" tag="span">
      Generated {absoluteLabel(analysis.generated_at)} by {analysis.generated_by ?? 'unknown'}
      {analysis.model_identifier ? ` · ${analysis.model_identifier}` : ''}
    </Text>
    {analysis.newer_event_count > 0 && (
      <Pill outline pillColor="yellow">
        <Text size="xs" tag="span">
          {plural(analysis.newer_event_count, 'newer event', 'newer events')} · re-run to include
        </Text>
      </Pill>
    )}
  </div>
);

const WhatHappened = ({ teacher }: { teacher: LiveSessionAnalysis['report']['teacher'] }) => {
  const counts = [
    teacher.drops > 0 && `The teacher lost connection ${plural(teacher.drops, 'time', 'times')}.`,
    teacher.dropped_navs > 0
      && `${plural(teacher.dropped_navs, 'slide change', 'slide changes')} never reached the students.`,
  ].filter(Boolean).join(' ');
  const text = teacher.notes || counts;
  if (!text) return null;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <SectionHeading>What happened</SectionHeading>
      <Text className="tw-text-tci-black tw-m-0" size="sm">{text}</Text>
    </div>
  );
};

const OpenQuestions = ({ questions }: { questions: string[] }) => {
  if (questions.length === 0) return null;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <SectionHeading>Not explained yet</SectionHeading>
      <ul className="tw-m-0 tw-pl-5 tw-flex tw-flex-col tw-gap-1">
        {questions.map(question => (
          <li key={question}><Text className="tw-text-tci-black tw-m-0" size="sm">{question}</Text></li>
        ))}
      </ul>
    </div>
  );
};

const sortVerdicts = (verdicts: AnalysisVerdict[]) => (
  [...verdicts].sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state))
);

// Advisory AI diagnosis of the session, above the chart, in the shape support staff asked for:
// bottom line, what happened, what each student saw, what is still unexplained. Every cited event
// links into the timeline so the model's work can be checked against the rows it read.
const AnalysisPanel = ({
  analysis, error, isRunning, students, onEvidenceClick, onRun, onSelectStudent,
}: AnalysisPanelProps) => {
  const studentById = new Map(students.map(s => [s.id, s]));
  let buttonLabel = 'Summarize with AI';
  if (isRunning) buttonLabel = 'Summarizing…';
  else if (analysis) buttonLabel = 'Re-run summary';

  return (
    <section aria-label="AI summary" className={PANEL_CLASS}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Text className="tw-text-tci-black tw-m-0" size="sm" tag="h2" variant="label">AI summary</Text>
          <Pill outline pillColor="grey"><Text size="xs" tag="span">Advisory</Text></Pill>
        </div>
        <Button small disabled={isRunning} outlineStyle={Boolean(analysis)} onClick={onRun}>{buttonLabel}</Button>
      </div>

      {isRunning && (
        <Text className="tw-text-very-dark-grey tw-m-0 tw-animate-pulse" size="sm">
          Summarizing… this usually takes 20 to 40 seconds.
        </Text>
      )}
      {error && (
        <div role="alert">
          <Text className="tw-text-red-700 tw-m-0" size="sm">{error}</Text>
        </div>
      )}

      {!analysis && !isRunning && !error && (
        <Text className="tw-text-very-dark-grey tw-m-0" size="sm">{PRIVACY_NOTE}</Text>
      )}

      {analysis && (
        <>
          <GeneratedMeta analysis={analysis} />
          <div className="tw-flex tw-flex-col tw-gap-1">
            <SectionHeading>Bottom line</SectionHeading>
            <Text className="tw-text-tci-black tw-m-0" size="sm">{analysis.report.summary}</Text>
          </div>
          <WhatHappened teacher={analysis.report.teacher} />
          <div className="tw-flex tw-flex-col tw-gap-1">
            <SectionHeading>What each student saw</SectionHeading>
            <ol className="tw-list-none tw-m-0 tw-p-0 tw-divide-y tw-divide-gray-100">
              {sortVerdicts(analysis.report.verdicts).map(verdict => (
                <VerdictRow
                  key={verdict.student_id}
                  student={studentById.get(verdict.student_id)}
                  verdict={verdict}
                  onEvidenceClick={onEvidenceClick}
                  onSelectStudent={onSelectStudent}
                />
              ))}
            </ol>
          </div>
          <OpenQuestions questions={analysis.report.open_questions} />
          <Text className="tw-text-gray-400 tw-m-0" size="xs">{PRIVACY_NOTE}</Text>
        </>
      )}
    </section>
  );
};

export default AnalysisPanel;
